

if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      this.setupEventDelegation();
    }

    setupEventDelegation() {
      // Event delegation for variant inputs
      this.addEventListener('change', (event) => {
        const target = event.target;
        const isVariantInput = target.matches('input[type="radio"], input[type="checkbox"], select') &&
                               (target.closest('variant-radios') || target.closest('variant-selects'));

        if (isVariantInput) {
          const variantPicker = target.closest('variant-radios') || target.closest('variant-selects');
          if (variantPicker && typeof variantPicker.onVariantChange === 'function') {
            try {
              variantPicker.onVariantChange();
            } catch (error) {
              console.error('Error calling onVariantChange:', error);
            }
            this.updateGalleryForVariant(variantPicker.currentVariant);
          }
        }
      }, true);
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      this.modalContent.replaceChildren(this.buildLoadingPlaceholder());
      super.show(opener);

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.moveProductTitleBeforeMediaOnMobile();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();

          // Force eager loading for all images
          const images = this.modalContent.querySelectorAll('img[loading="lazy"]');
          images.forEach(img => {
            img.loading = 'eager';
          });

          this.preventVariantURLSwitching();
          this.preventVariantRedirects();
          this.initializeMediaGallery();
          this.initializeVariantSelection();
          this.ensureVariantEventListeners();
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    buildLoadingPlaceholder() {
      const template = this.querySelector('template.quick-add-spinner-template');
      if (template && template.content) {
        return template.content.firstElementChild.cloneNode(true);
      }
      return document.createElement('div');
    }

    loadProductInModal(productHandle) {
      const modalElement = this.querySelector('.quick-add-modal__content');
      let loadingOverlay = null;
      if (modalElement) {
        modalElement.style.pointerEvents = 'none';
        loadingOverlay = this.buildLoadingPlaceholder();
        loadingOverlay.classList.add('quick-add-loading-overlay');
        modalElement.appendChild(loadingOverlay);
      }

      this.destroySlickCarousels();

      fetch(`/products/${productHandle}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load product: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

          if (!this.productElement) {
            throw new Error('Product section not found in response');
          }

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.moveProductTitleBeforeMediaOnMobile();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          this.preventVariantRedirects();

          // Force eager loading for all images
          const images = this.modalContent.querySelectorAll('img[loading="lazy"]');
          images.forEach(img => {
            img.loading = 'eager';
            if (!img.complete && img.src) {
              const tempImg = new Image();
              tempImg.src = img.src;
            }
          });

          setTimeout(() => {
            this.initializeMediaGallery();
            this.initializeVariantSelection();
            this.ensureVariantEventListeners();
            setTimeout(removeLoadingOverlay, 350);
          }, 100);
        })
        .catch((error) => {
          console.error('Error loading product in modal:', error);
          alert('Sorry, there was an error loading this product. Please try again.');
          removeLoadingOverlay();
        });

      function removeLoadingOverlay() {
        if (modalElement) {
          modalElement.style.pointerEvents = 'auto';
          if (loadingOverlay && loadingOverlay.parentNode) {
            loadingOverlay.parentNode.removeChild(loadingOverlay);
            loadingOverlay = null;
          }
        }
      }
    }

    destroySlickCarousels() {
      if (typeof $ !== 'undefined' && $.fn.slick) {
        const $slider = $(this.modalContent).find('.product-images-slider');
        const $thumbnails = $(this.modalContent).find('.slider-nav-thumbnails');

        if ($slider.hasClass('slick-initialized')) {
          $slider.slick('unslick');
        }

        if ($thumbnails.hasClass('slick-initialized')) {
          $thumbnails.slick('unslick');
        }
      }
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinject script tags to allow execution
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        if (oldScriptTag.textContent) {
          newScriptTag.textContent = oldScriptTag.textContent;
        } else if (oldScriptTag.innerHTML) {
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        }
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    moveProductTitleBeforeMediaOnMobile() {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const product = this.modalContent.querySelector('.product');
      if (!product) return;

      const mediaWrapper = product.querySelector('.product__media-wrapper');
      const productTitle = product.querySelector('.product__info-wrapper .product__title');

      if (!mediaWrapper || !productTitle) return;

      product.insertBefore(productTitle, mediaWrapper);
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    preventVariantRedirects() {
      // Handle color options that link to different products
      const optionContainers = this.modalContent.querySelectorAll('.option-container[onclick]');

      optionContainers.forEach(container => {
        container.removeAttribute('onclick');

        container.addEventListener('click', (e) => {
          const productHandle = container.getAttribute('data-product-handle');
          const isCurrentProduct = container.hasAttribute('data-current-product');

          // Load different product if it's a different color
          if (productHandle && !isCurrentProduct) {
            e.preventDefault();
            e.stopPropagation();
            this.loadProductInModal(productHandle);
            return;
          }

          // Do nothing if current product
          if (isCurrentProduct) {
            e.preventDefault();
          }
        });
      });
    }

    initializeMediaGallery() {
      // Use the existing Slick initialization pattern from general.js
      // Wait for images to be ready before initializing
      const productImagesSlider = this.modalContent.querySelector('.product-images-slider');
      const sliderNavThumbnails = this.modalContent.querySelector('.slider-nav-thumbnails');

      if (!productImagesSlider || typeof $ === 'undefined' || !$.fn.slick) {
        return;
      }

      const $slider = $(productImagesSlider);
      const $thumbnails = $(sliderNavThumbnails);

      // Don't re-initialize if already initialized
      if ($slider.hasClass('slick-initialized')) {
        return;
      }

      // Wait a bit for images to render, then initialize
      setTimeout(() => {
        try {
          // Initialize main slider - match existing settings from general.js
          $slider.slick({
            slidesToScroll: 1,
            slidesToShow: 1,
            arrows: true,
            dots: false,
            asNavFor: $thumbnails,
            vertical: false,
            verticalSwiping: false,
            infinite: true,
            draggable: false,
            prevArrow: '<button type="button" class="slick-prev custom-arrow"><code><</code></button>',
            nextArrow: '<button type="button" class="slick-next custom-arrow"><code>></code></button>',
            responsive: [
              {
                breakpoint: 750,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  arrows: true,
                  draggable: true,
                  infinite: true
                }
              }
            ]
          });

          // Initialize thumbnails - match existing settings from general.js
          if (sliderNavThumbnails && !$thumbnails.hasClass('slick-initialized')) {
            $thumbnails.slick({
              slidesToScroll: 1,
              asNavFor: $slider,
              arrows: false,
              dots: false,
              focusOnSelect: true,
              variableWidth: true,  // Match original
              vertical: true,
              infinite: false,
              responsive: [
                {
                  breakpoint: 767,
                  settings: {
                    vertical: false,
                  }
                }
              ]
            });
          }

          // Match the active thumbnail logic from general.js (scoped to modal)
          $thumbnails.find(".slick-slide").removeClass("slick-active");
          $thumbnails.find(".slick-slide").eq(0).addClass("slick-active");

          $slider.on("beforeChange", function (event, slick, currentSlide, nextSlide) {
            $thumbnails.find(".slick-slide").removeClass("slick-active");
            $thumbnails.find(".slick-slide").eq(nextSlide).addClass("slick-active");
          });

          // Initialize zoom functionality after Slick is ready
          setTimeout(() => {
            this.initializeZoom();
          }, 100);

        } catch (error) {
          console.error('Error initializing Slick:', error);
        }
      }, 200);  // Give images time to render
    }

    initializeZoom() {
      // Initialize zoom on product images (desktop only, matching general.js pattern)
      if (typeof $ === 'undefined' || !$.fn.zoom || $(window).width() <= 992) {
        return;
      }

      // Find all product images in the modal that aren't already zoomed
      const $images = $(this.modalContent).find("img.product-image-image").filter(function() {
        return !$(this).parent().hasClass('zoom-wrapper');
      });

      $images.each(function() {
        const $img = $(this);

        // Wrap image in a span that matches its dimensions exactly
        $img.wrap('<span class="zoom-wrapper" style="display:inline-block; position: relative; overflow: hidden;"></span>');
        const $wrapper = $img.parent();

        // Set wrapper dimensions to match image
        $wrapper.css({
          width: $img.outerWidth() + 'px',
          height: $img.outerHeight() + 'px'
        });

        $img.css("display", "block");

        // Apply zoom to the wrapper
        $wrapper.zoom({ on: "grab" });
      });
    }

    updateGalleryForVariant(variant) {
      if (!variant || typeof $ === 'undefined' || !$.fn.slick) return;

      const $slider = $(this.modalContent).find('.product-images-slider');
      const $thumbnails = $(this.modalContent).find('.slider-nav-thumbnails');
      if (!$slider.length || !$slider.hasClass('slick-initialized')) return;

      const colorAlt = variant.featured_media && variant.featured_media.alt;
      if (!colorAlt) return;

      $slider.slick('slickUnfilter');
      if ($thumbnails.hasClass('slick-initialized')) $thumbnails.slick('slickUnfilter');

      const selector = '[thumbnail-color="' + colorAlt + '"]';
      $slider.slick('slickFilter', selector).slick('refresh').slick('slickGoTo', 0);
      if ($thumbnails.hasClass('slick-initialized')) {
        $thumbnails.slick('slickFilter', selector).slick('refresh');
      }
    }

    initializeVariantSelection() {
      // Update checked classes on variant labels for visual feedback
      const fieldsets = this.modalContent.querySelectorAll('fieldset');
      fieldsets.forEach(fieldset => {
        const radios = fieldset.querySelectorAll('input[type="radio"]');
        radios.forEach(radio => {
          radio.addEventListener('change', function() {
            if (this.checked) {
              const fieldset = this.closest('fieldset');
              if (fieldset) {
                fieldset.querySelectorAll('.variant-color-label').forEach(label => {
                  label.classList.remove('checked');
                });
                const container = this.closest('.option-container');
                if (container) {
                  const label = container.querySelector('.variant-color-label');
                  if (label) {
                    label.classList.add('checked');
                  }
                }
              }
            }
          });
        });
      });
    }

    ensureVariantEventListeners() {
      const variantRadios = this.modalContent.querySelector('variant-radios');
      const variantSelects = this.modalContent.querySelector('variant-selects');

      [variantRadios, variantSelects].forEach(variantPicker => {
        if (!variantPicker) return;

        if (!variantPicker.onVariantChange || typeof variantPicker.onVariantChange !== 'function') {
          console.warn('onVariantChange method not found on', variantPicker.tagName);
        }
      });
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
